import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Globe, KeyRound, Check, Copy, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { runScan, verifyOwnership, createChallenge } from "@/lib/api";
import {
  saveScan,
  loadVerification,
  saveVerification,
  clearVerification,
} from "@/lib/history";

const DEMO_DOMAINS = new Set(["example.com", "example.net", "example.org"]);
const PRESETS = [{ domain: "example.com", label: "example.com (demo)" }];

const STAGES = [
  "Resolving DNS & TLS certificate",
  "Fetching HTTPS response headers",
  "Checking SPF & DMARC records",
  "Computing security posture score",
];

const CopyField = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 min-w-0 font-mono text-xs sm:text-[13px] text-cyan-300 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 break-all">
        {value}
      </code>
      <button
        type="button"
        onClick={copy}
        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2.5 text-xs text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 transition"
        data-testid={testId}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
};

export const ScannerForm = () => {
  const [domain, setDomain] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(0);
  const [verification, setVerification] = useState(null); // { token, verified }
  const [verifying, setVerifying] = useState(false);
  const navigate = useNavigate();

  const normalized = domain.trim().toLowerCase();
  const isDemo = DEMO_DOMAINS.has(normalized);

  // Load any previously stored verification for the current domain
  useEffect(() => {
    if (!normalized) {
      setVerification(null);
      return;
    }
    if (isDemo) {
      setVerification({ token: null, verified: true, demo: true });
      return;
    }
    setVerification(loadVerification(normalized));
  }, [normalized, isDemo]);

  const verified = Boolean(verification?.verified);
  const token = verification?.token || null;
  const challengeId = verification?.challengeId || null;
  const recordName = normalized ? `_shieldpilot-challenge.${normalized}` : "";

  const requestChallenge = async (regenerate) => {
    if (!normalized || isDemo) return;
    setVerifying(true);
    try {
      const ch = await createChallenge({ domain: normalized });
      const data = {
        challengeId: ch.challenge_id,
        token: ch.expected_value,
        challengeExpiresAt: ch.expires_at,
        verified: false,
        createdAt: new Date().toISOString(),
      };
      saveVerification(normalized, data);
      setVerification(data);
      if (regenerate) toast.info("New token generated - update your TXT record");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Could not start verification");
    } finally {
      setVerifying(false);
    }
  };

  const startVerification = () => {
    if (!normalized) {
      toast.error("Enter a domain first");
      return;
    }
    requestChallenge(false);
  };

  const regenerateToken = () => {
    if (!normalized || isDemo) return;
    clearVerification(normalized);
    requestChallenge(true);
  };

  const resetVerification = (message) => {
    clearVerification(normalized);
    setVerification(null);
    if (message) toast.error(message);
  };

  const checkVerification = async () => {
    if (!normalized || !challengeId) return;
    setVerifying(true);
    try {
      const res = await verifyOwnership({ domain: normalized, challengeId });
      if (res.verified) {
        const data = {
          challengeId,
          token,
          verified: true,
          scanToken: res.scan_token,
          scanTokenExpiresAt: res.scan_token_expires_at,
          verifiedAt: new Date().toISOString(),
        };
        saveVerification(normalized, data);
        setVerification(data);
        toast.success("Ownership verified");
      } else {
        toast.error(res.message || "Verification failed");
      }
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message || "Verification failed";
      if (status === 400 || status === 410) {
        resetVerification(detail);
      } else {
        toast.error(detail);
      }
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!normalized) {
      toast.error("Enter a domain to scan");
      return;
    }
    if (!authorized) {
      toast.error("Confirm you are authorized to assess this domain");
      return;
    }
    if (!isDemo && !verified) {
      toast.error("Verify domain ownership before scanning");
      return;
    }
    setLoading(true);
    setStage(0);
    const ticker = setInterval(() => {
      setStage((s) => Math.min(STAGES.length - 1, s + 1));
    }, 900);
    try {
      const result = await runScan({
        domain: normalized,
        authorized: true,
        scanToken: isDemo ? null : verification?.scanToken,
      });
      saveScan(result);
      toast.success(`Scan complete · ${result.domain}`);
      navigate(`/dashboard?id=${result.id}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Scan failed";
      if (!isDemo && err?.response?.status === 403) {
        resetVerification(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      clearInterval(ticker);
      setLoading(false);
      setStage(0);
    }
  };

  const showVerificationPanel = useMemo(
    () => Boolean(normalized) && !isDemo,
    [normalized, isDemo]
  );

  return (
    <form
      onSubmit={submit}
      className="relative rounded-2xl border border-slate-800 bg-slate-900/80 p-5 sm:p-7 backdrop-blur-sm"
      data-testid="scanner-form"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-cyan-400 font-bold mb-3">
        <ShieldCheck className="h-3.5 w-3.5" />
        Non-destructive scanner
      </div>
      <label className="block">
        <span className="sr-only">Domain</span>
        <div className="relative">
          <Globe className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
          <input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="yourdomain.com"
            autoComplete="off"
            spellCheck="false"
            disabled={loading}
            className="w-full h-14 pl-12 pr-4 rounded-xl bg-slate-950 border border-slate-800 text-slate-100 placeholder:text-slate-600 font-mono text-base focus:outline-none focus:ring-2 focus:ring-cyan-500/60 focus:border-cyan-500/50 transition"
            data-testid="domain-input-field"
          />
        </div>
      </label>

      <div className="mt-3 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.domain}
            type="button"
            onClick={() => setDomain(p.domain)}
            disabled={loading}
            className="rounded-full border border-slate-800 bg-slate-950/60 px-3 py-1 text-xs font-mono text-slate-400 hover:text-cyan-300 hover:border-cyan-500/40 transition"
            data-testid={`quick-domain-preset-${p.domain.replace(/\./g, "-")}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
        <Checkbox
          id="auth-check"
          checked={authorized}
          onCheckedChange={(v) => setAuthorized(Boolean(v))}
          disabled={loading}
          className="mt-0.5 border-slate-600 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
          data-testid="domain-ownership-checkbox"
        />
        <span className="text-sm text-slate-300 leading-relaxed">
          I confirm I own this domain, or have written authorization to assess it.
          ShieldPilot runs passive checks only - no exploitation, no credential testing, no uploads.
        </span>
      </label>

      {/* Verification panel */}
      {showVerificationPanel && (
        <div
          className={`mt-5 rounded-xl border p-4 ${
            verified
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-cyan-500/25 bg-slate-950/60"
          }`}
          data-testid="verification-panel"
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <KeyRound
                className={`h-4 w-4 ${verified ? "text-emerald-400" : "text-cyan-400"}`}
              />
              <span className="text-sm font-semibold text-slate-100">
                {verified ? "Ownership verified" : "Verify domain ownership"}
              </span>
            </div>
            {verified ? (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-400"
                data-testid="verification-status-verified"
              >
                <Check className="h-3 w-3" /> Verified
              </span>
            ) : (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400"
                data-testid="verification-status-pending"
              >
                Pending
              </span>
            )}
          </div>

          {!token && !verified && (
            <>
              <p className="mt-3 text-xs text-slate-400 leading-relaxed">
                We generate a one-time challenge for this domain. It expires in 15 minutes and is only valid from this browser session.
              </p>
              <button
                type="button"
                onClick={startVerification}
                disabled={!normalized || loading || verifying}
                className="mt-3 inline-flex items-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm px-4 py-2 transition disabled:opacity-60"
                data-testid="start-verification-button"
              >
                <KeyRound className="h-4 w-4" />
                Start verification
              </button>
            </>
          )}

          {token && (
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1.5">
                  1. Add this DNS TXT record
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-2 text-xs text-slate-400">
                  <span className="font-mono text-slate-500 sm:pt-2">Name</span>
                  <CopyField value={recordName} testId="copy-record-name" />
                  <span className="font-mono text-slate-500 sm:pt-2">Type</span>
                  <code className="font-mono text-cyan-300 bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 w-fit">
                    TXT
                  </code>
                  <span className="font-mono text-slate-500 sm:pt-2">Value</span>
                  <CopyField value={token} testId="copy-record-value" />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {!verified && (
                  <button
                    type="button"
                    onClick={checkVerification}
                    disabled={verifying || loading}
                    className="inline-flex items-center gap-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold text-sm px-4 py-2 transition disabled:opacity-60"
                    data-testid="check-verification-button"
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Checking DNS
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4" />
                        2. Check verification
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={regenerateToken}
                  disabled={verifying || loading}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 text-slate-300 hover:text-cyan-300 hover:border-cyan-500/40 text-xs px-3 py-2 transition"
                  data-testid="regenerate-token-button"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                  New token
                </button>
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                DNS propagation can take a few minutes. Once verified, scanning stays enabled for 30 minutes before you need to verify again.
              </p>
            </div>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || (!isDemo && !verified)}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-semibold h-12 transition cyan-glow"
        data-testid="start-scan-button"
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {STAGES[stage]}
          </>
        ) : !isDemo && !verified ? (
          <>Verify ownership to enable scan</>
        ) : (
          <>Run security scan</>
        )}
      </button>

      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        Blocks localhost and private-network targets. example.com is a reserved demo domain and bypasses verification. ShieldPilot never claims a site is unhackable.
      </p>
    </form>
  );
};

export default ScannerForm;
